import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '@/api/services/auth';
import type {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ResendVerificationRequest,
  ResetPasswordRequest,
  UserCreate,
  UserLogin,
} from '@/api/types';
import { clearAuthToken, getAuthToken, setAuthToken } from '@/lib/auth';
import { clearCreatedLaunch } from '@/features/fit-launcher/lib/createdLaunch';
import { clearManagedLaunch } from '@/features/managed/lib/launchHandover';
import { clearPurchaseIntent } from '@/features/billing/lib/purchaseIntent';

export const meQueryKey = ['auth', 'me'] as const;

export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: authService.me,
    enabled: !!getAuthToken(),
    staleTime: 60_000,
    retry: false,
  });
}

/** Helper because the spec returns is_admin as a number (0/1). */
export function useIsAdmin() {
  const { data } = useMe();
  return data?.is_admin === 1;
}

/**
 * For a click handler that must not act on a `/auth/me` still loading:
 * resolves whether the user is an admin, fetching `me` if it is not cached.
 * False without a token or when the lookup fails.
 */
export function useResolveIsAdmin() {
  const qc = useQueryClient();
  return async (): Promise<boolean> => {
    if (!getAuthToken()) return false;
    try {
      const me = await qc.fetchQuery({
        queryKey: meQueryKey,
        queryFn: authService.me,
        staleTime: 60_000,
      });
      return me?.is_admin === 1;
    } catch {
      return false;
    }
  };
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserLogin) => authService.login(body),
    onSuccess: (data) => {
      setAuthToken(data.access_token);
      qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: (body: UserCreate) => authService.signup(body),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (body: ForgotPasswordRequest) => authService.forgotPassword(body),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (body: ResetPasswordRequest) => authService.resetPassword(body),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => authService.verifyEmail(token),
  });
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (body: ResendVerificationRequest) => authService.resendVerification(body),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: ChangePasswordRequest) => authService.changePassword(body),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => authService.deleteAccount(),
    onSuccess: () => {
      clearAuthToken();
      clearCreatedLaunch();
      clearManagedLaunch();
      clearPurchaseIntent();
      qc.clear();
    },
  });
}

/** Where the removed claim page kept a code in this tab (ADR-0024 decision 13). */
const LEGACY_PENDING_CLAIM_KEY = 'pendingClaimCode';

/** One-time cleanup of a claim code an older release left in this tab.
 *  Remove it after the release that drops the claim tables. */
function clearLegacyPendingClaim() {
  try {
    window.sessionStorage.removeItem(LEGACY_PENDING_CLAIM_KEY);
  } catch {
    /* storage blocked */
  }
}

export function useLogout() {
  const qc = useQueryClient();
  return () => {
    clearAuthToken();
    clearCreatedLaunch();
    clearManagedLaunch();
    clearLegacyPendingClaim();
    clearPurchaseIntent();
    qc.clear();
  };
}
